import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, Code2, ChevronDown, ChevronUp } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { services } from "@/data/services";
import { coreValues } from "@/data/core-values";
import servicesHeroBg from "@/assets/services-hero-bg.png";
import dotsDecoration from "@/assets/dots-decoration.png";
import dotsDecorationRight from "@/assets/dots-decoration-right.png";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

export const Route = createFileRoute("/services")({
  component: ServicesPage,
  head: () => ({
    meta: [
      { title: "IT Services in Montclair — SimpleTech" },
      { name: "description", content: "Managed IT, tech support, VoIP, cybersecurity, web design, software integration, accessibility, and senior tech help in Montclair, CA." },
      { property: "og:title", content: "IT Services in Montclair — SimpleTech" },
      { property: "og:description", content: "One-stop IT services for Montclair businesses, consumers and seniors." },
      seoOgUrl("/services"),
      { property: "og:type", content: "website" },
    ],
    links: [seoCanonicalLink("/services")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: services.map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Service",
              name: s.shortTitle,
              description: s.seoDescription ?? s.excerpt,
              url: canonical(`/services/${s.slug}`),
              provider: {
                "@type": "LocalBusiness",
                name: "SimpleTech",
                telephone: "+1-909-255-9112",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "8880 N Benson Ave #121",
                  addressLocality: "Montclair",
                  addressRegion: "CA",
                  postalCode: "91763",
                  addressCountry: "US",
                },
              },
              areaServed: "Montclair, CA",
            },
          })),
        }),
      },
    ],
  }),
});

function ServicesPage() {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader active="services" />

      <section className="relative min-h-[230px] md:min-h-[315px] text-white overflow-hidden flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-bottom"
          style={{ backgroundImage: `url(${servicesHeroBg})` }}
        />
        <div className="absolute inset-0 bg-[#0b0a3d]/40" />
        <div className="relative max-w-7xl mx-auto px-8 w-full py-12 flex items-center justify-between">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white">Services</h1>
          <nav aria-label="Breadcrumb" className="hidden md:block">
            <ol className="flex items-center gap-2 text-white/80 text-sm font-medium tracking-wide">
              <li><Link to="/" className="hover:text-white transition-colors">HOME</Link></li>
              <li aria-hidden className="text-white/50">•</li>
              <li className="text-white">SERVICES</li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-5xl mx-auto px-8 py-20 text-center">
        <div className="text-indigo-600 font-bold tracking-widest text-sm mb-3">LEADING YOU TO SUCCESS</div>
        <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900">One-Stop IT Support</h2>
        <p className="mt-6 text-slate-600 leading-relaxed">
          At SimpleTech, we provide you with the highest quality IT services. From expert technical support
          to seamless software integration and custom website solutions, there's no limit to our capabilities!
        </p>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Discover all that we have in store for you by checking out our service offerings below.
        </p>
      </section>

      {/* Service cards */}
      <section className="max-w-7xl mx-auto px-8 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map(({ icon: Icon, slug, shortTitle, excerpt }) => (
            <Link
              key={slug}
              {...(slug === "managed-it"
                ? { to: "/services/managed-it-services" as const }
                : { to: "/services/$slug" as const, params: { slug } })}
              className="group bg-white hover:-translate-y-1 transition-all rounded-2xl p-8 border border-slate-100 shadow-md hover:shadow-2xl flex flex-col"
            >
              <Icon className="w-10 h-10 text-indigo-600 mb-5" strokeWidth={1.5} />
              <h3 className="text-xl font-bold text-slate-900 mb-3">{shortTitle}</h3>
              <p className="text-slate-600 text-sm leading-relaxed flex-1">{excerpt}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold tracking-wider text-sky-500 group-hover:text-sky-600">
                LEARN MORE <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Value cards */}
      <section className="max-w-7xl mx-auto px-8 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              n: "01",
              title: "Focus On Business",
              body: "SimpleTech is your ally in IT, passionate about streamlining and upgrading your business operations — so you can focus on what matters most. Let us show you how our top-tier services will revolutionize the way that technology impacts both work and life.",
              link: "Rise to the top",
            },
            {
              n: "02",
              title: "Always Available",
              body: "At SimpleTech, our IT experts are eager to help you upgrade your tech environment. We have multiple support specialists available who confidently solve any software integration or IT issue — making sure all of your technology needs run smoothly!",
              link: "Count on us",
            },
            {
              n: "03",
              title: "Solutions For You",
              body: "At SimpleTech, we provide the highest quality IT services at an unbeatable price. Our commitment to transparency and ease of use makes us your go-to for all technological needs!",
              link: "Transparent Pricing",
            },
          ].map((c) => (
            <div key={c.n} className="relative overflow-hidden rounded-xl bg-[#2d1f5f] text-white p-9 min-h-[420px] flex flex-col">
              <span aria-hidden className="absolute top-4 left-6 text-7xl font-extrabold text-white/5 select-none leading-none">
                {c.n}
              </span>
              <div className="absolute -right-10 -bottom-12 w-56 h-56 rounded-full bg-white/[0.04] pointer-events-none" />
              <h3 className="relative text-2xl font-extrabold mb-5">{c.title}</h3>
              <p className="relative text-white/75 leading-relaxed text-[15px] flex-1">{c.body}</p>
              <Link
                to="/contact"
                className="relative mt-6 inline-block text-sky-400 hover:text-sky-300 font-semibold text-sm tracking-wide"
              >
                {c.link}
              </Link>
              <Code2 className="absolute bottom-6 right-6 w-10 h-10 text-sky-300/70" strokeWidth={1.5} />
            </div>
          ))}
        </div>
      </section>

      {/* Core Values accordion */}
      <section className="relative overflow-hidden bg-white">
        <img src={dotsDecoration} alt="" aria-hidden="true" width={288} height={288} className="absolute left-0 top-10 w-72 opacity-70 pointer-events-none" />
        <img src={dotsDecorationRight} alt="" aria-hidden="true" width={320} height={320} className="absolute right-0 bottom-0 w-80 opacity-70 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-8 py-20">
          <div className="text-indigo-600 font-bold tracking-widest text-sm mb-3">WHO WE ARE</div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-10">Our Core Values</h2>
          <div className="space-y-3">
            {coreValues.map((v, i) => {
              const open = openIndex === i;
              return (
                <div key={v.title} className="bg-white border border-slate-200 rounded-md overflow-hidden">
                  <button
                    onClick={() => setOpenIndex(open ? -1 : i)}
                    className={`w-full flex items-center justify-between px-6 py-4 text-left font-bold ${open ? "bg-sky-50 text-sky-600" : "text-slate-900"}`}
                  >
                    <span>{v.title}</span>
                    {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {open && (
                    <div className="px-6 pb-6 pt-2 text-slate-600 leading-relaxed whitespace-pre-line">
                      {v.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
